import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, User, Trophy, Target, Award, MessageCircle, CheckCircle, XCircle, ExternalLink } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import ThemeToggle from "@/components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile({ user, logout }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [givenName, setGivenName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [membershipNumber, setMembershipNumber] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [playingPosition, setPlayingPosition] = useState("");
  const [joinedSccDate, setJoinedSccDate] = useState("");
  const [goalsScored, setGoalsScored] = useState(0);
  const [assists, setAssists] = useState(0);
  const [motmAwards, setMotmAwards] = useState(0);
  const [saving, setSaving] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState(null);
  
  const isSectionMember = user?.member_type === "section" || user?.role === "captain" || user?.role === "convenor";
  const isAcademyPlayer = user?.roles?.includes("academy_player");
  const isAcademyParentOnly = user?.roles?.includes("academy_parent") && !isSectionMember && !isAcademyPlayer;
  const showPlayerStats = isSectionMember || isAcademyPlayer;

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setDisplayName(user.display_name || "");
      setGivenName(user.given_name || "");
      setLastName(user.last_name || "");
      setPhone(user.phone || "");
      setMembershipNumber(user.membership_number || "");
      setBirthdate(user.birthdate || "");
      setPlayingPosition(user.playing_position || "");
      setGoalsScored(user.goals_scored || 0);
      setAssists(user.assists || 0);
      setMotmAwards(user.motm_awards || 0);
      if (user.joined_date) {
        const date = new Date(user.joined_date);
        setJoinedSccDate(date.toISOString().split('T')[0]);
      }
      
      // Fetch notification settings for Telegram status
      fetchNotificationSettings();
    }
  }, [user]);

  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/notification-settings`, { withCredentials: true });
      setNotificationSettings(response.data);
    } catch (error) {
      console.error("Error fetching notification settings:", error);
    }
  };

  const calculateAge = (birthdate) => {
    if (!birthdate) return null;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getJoinedYear = () => {
    if (user?.joined_date) {
      return new Date(user.joined_date).getFullYear();
    }
    if (user?.created_at) {
      return new Date(user.created_at).getFullYear();
    }
    return null;
  };

  const updateProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/update-profile`, {
        name,
        display_name: displayName,
        given_name: givenName,
        last_name: lastName,
        phone,
        membership_number: membershipNumber,
        birthdate,
        playing_position: playingPosition,
        joined_scc_date: joinedSccDate,
        goals_scored: parseInt(goalsScored) || 0,
        assists: parseInt(assists) || 0,
        motm_awards: parseInt(motmAwards) || 0
      }, { withCredentials: true });
      
      toast.success("Profile updated successfully!");
      window.location.reload();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate("/dashboard")}
            variant="ghost"
            className="text-slate-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>
          <ThemeToggle />
        </div>

        <Card className="p-6 md:p-8 bg-slate-900/50 border border-white/5 rounded-xl">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-6 font-display">My Profile</h1>

          {/* Profile Summary */}
          <div className="bg-slate-800/50 p-5 rounded-xl mb-8 border border-white/5">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-900 to-amber-600 rounded-xl flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-xl text-white">{name || user?.name}</h2>
                <p className="text-slate-400 text-sm">{user?.email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {calculateAge(birthdate) && (
                <div>
                  <p className="text-slate-400">Age</p>
                  <p className="text-white font-medium">{calculateAge(birthdate)} years old</p>
                </div>
              )}
              {showPlayerStats && playingPosition && (
                <div>
                  <p className="text-slate-400">Position</p>
                  <p className="text-white font-medium">{playingPosition}</p>
                </div>
              )}
              {isSectionMember && getJoinedYear() && (
                <div>
                  <p className="text-slate-400">Member Since</p>
                  <p className="text-white font-medium">{getJoinedYear()}</p>
                </div>
              )}
              {isAcademyParentOnly && (
                <div>
                  <p className="text-slate-400">Role</p>
                  <p className="text-white font-medium">Academy Parent</p>
                </div>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          {showPlayerStats && (
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-slate-800/50 border border-white/5 rounded-xl p-4 text-center">
                <Trophy className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{goalsScored}</p>
                <p className="text-slate-400 text-xs">Goals</p>
              </div>
              <div className="bg-slate-800/50 border border-white/5 rounded-xl p-4 text-center">
                <Target className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{assists}</p>
                <p className="text-slate-400 text-xs">Assists</p>
              </div>
              <div className="bg-slate-800/50 border border-white/5 rounded-xl p-4 text-center">
                <Award className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{motmAwards}</p>
                <p className="text-slate-400 text-xs">MOTM</p>
              </div>
            </div>
          )}

          {/* Telegram Connection Status */}
          <div className="mb-8">
            <Link to="/notification-settings">
              <div className={`p-4 rounded-xl border cursor-pointer transition-all hover:border-opacity-70 ${
                notificationSettings?.telegram_linked
                  ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/15'
                  : 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/15'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      notificationSettings?.telegram_linked ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                    }`}>
                      <MessageCircle className={`w-5 h-5 ${
                        notificationSettings?.telegram_linked ? 'text-emerald-400' : 'text-amber-400'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">Telegram Notifications</span>
                        {notificationSettings?.telegram_linked ? (
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-amber-400" />
                        )}
                      </div>
                      <p className="text-sm text-slate-400">
                        {notificationSettings?.telegram_linked
                          ? `Connected${notificationSettings?.telegram_username ? ` as @${notificationSettings.telegram_username}` : ''}`
                          : 'Not connected - Tap to set up instant notifications'}
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="w-5 h-5 text-slate-500" />
                </div>
              </div>
            </Link>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Email</label>
              <Input
                value={user?.email || ""}
                disabled
                className="bg-slate-800/50 border-slate-700 text-slate-400"
              />
              <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Given Name</label>
                <Input
                  value={givenName}
                  onChange={(e) => setGivenName(e.target.value)}
                  placeholder="John"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 focus:border-yellow-500/50"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Last Name</label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 focus:border-yellow-500/50"
                />
              </div>
            </div>

            {/* Display Name */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <label className="text-sm font-medium text-blue-400 mb-2 block">
                Display Name
                <span className="text-slate-400 font-normal ml-2">(How others see you)</span>
              </label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={`${givenName} ${lastName}`.trim() || "Enter preferred name"}
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500/50"
              />
              <p className="text-xs text-slate-400 mt-2">
                This is how your name will appear in team lists, game signups, and WhatsApp exports. 
                Leave blank to use your full name.
              </p>
              {displayName && (
                <p className="text-xs text-blue-400 mt-1">
                  Preview: <strong>{displayName}</strong>
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Date of Birth</label>
              <Input
                type="date"
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                className="bg-slate-800/50 border-slate-700 text-white focus:border-yellow-500/50"
              />
              {birthdate && (
                <p className="text-xs text-slate-400 mt-1">Age: {calculateAge(birthdate)} years</p>
              )}
            </div>

            {showPlayerStats && (
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Playing Position</label>
                <select
                  value={playingPosition}
                  onChange={(e) => setPlayingPosition(e.target.value)}
                  className="w-full p-2.5 bg-slate-800/50 border border-slate-700 rounded-md text-white focus:border-yellow-500/50 focus:outline-none"
                >
                  <option value="">Select position</option>
                  <option value="Goalkeeper">Goalkeeper</option>
                  <option value="Defender">Defender</option>
                  <option value="Midfielder">Midfielder</option>
                  <option value="Forward">Forward</option>
                  <option value="Striker">Striker</option>
                  <option value="Winger">Winger</option>
                </select>
              </div>
            )}

            {showPlayerStats && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Goals Scored</label>
                  <Input
                    type="number"
                    min="0"
                    value={goalsScored}
                    onChange={(e) => setGoalsScored(e.target.value)}
                    placeholder="0"
                    className="bg-slate-800/50 border-slate-700 text-white focus:border-yellow-500/50"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Assists</label>
                  <Input
                    type="number"
                    min="0"
                    value={assists}
                    onChange={(e) => setAssists(e.target.value)}
                    placeholder="0"
                    className="bg-slate-800/50 border-slate-700 text-white focus:border-yellow-500/50"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">MOTM Awards</label>
                  <Input
                    type="number"
                    min="0"
                    value={motmAwards}
                    onChange={(e) => setMotmAwards(e.target.value)}
                    placeholder="0"
                    className="bg-slate-800/50 border-slate-700 text-white focus:border-yellow-500/50"
                  />
                </div>
              </div>
            )}

            {isSectionMember && (
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Membership Number</label>
                <Input
                  value={membershipNumber}
                  onChange={(e) => setMembershipNumber(e.target.value)}
                  placeholder="Your SCC membership number"
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 focus:border-yellow-500/50"
                />
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">Phone Number</label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+65 1234 5678"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 focus:border-yellow-500/50"
              />
            </div>

            {isSectionMember && (
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">When Did You Join SCC?</label>
                <Input
                  type="date"
                  value={joinedSccDate}
                  onChange={(e) => setJoinedSccDate(e.target.value)}
                  className="bg-slate-800/50 border-slate-700 text-white focus:border-yellow-500/50"
                />
                <p className="text-xs text-slate-400 mt-1">This is when you joined SCC Football, not this portal</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Role</label>
                <Input
                  value={user?.role || ""}
                  disabled
                  className="bg-slate-800/50 border-slate-700 text-slate-400"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Member Type</label>
                <Input
                  value={user?.member_type === "section" ? "Section Member" : "Non-Section Member"}
                  disabled
                  className="bg-slate-800/50 border-slate-700 text-slate-400"
                />
              </div>
            </div>

            <Button
              onClick={updateProfile}
              disabled={saving}
              className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold py-3 mt-4"
            >
              {saving ? "Saving..." : "Update Profile"}
            </Button>
          </div>
        </Card>

        {/* Quick Links */}
        <Card className="p-6 mt-6 bg-slate-900/50 border border-white/5 rounded-xl">
          <h2 className="text-lg font-bold text-white mb-4">Quick Links</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              onClick={() => navigate("/notification-settings")}
              variant="outline"
              className="justify-start border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-yellow-500/50"
            >
              <span className="mr-2">🔔</span>
              Notification Settings
            </Button>
            <Button
              onClick={() => navigate("/calendar-settings")}
              variant="outline"
              className="justify-start border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-yellow-500/50"
            >
              <span className="mr-2">📅</span>
              Calendar Sync
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
